import { parseObject } from 'libs/object/object';
import { defaultLocale } from 'libs/locale';

const baseKey = '285fae9184424f63aba1be6cd16c573d';

interface HTTPResponse<T> {
  $ok: boolean;
  data: T;
  status: number;
  count?: number;
  next?: string;
  previous?: string;
}

export interface Params {
  lang: string | string[];
  credentials?: 'include';

  [key: string]: any;
}

export const fetcher = {
  // Use extends on the generic parameter to hint the compiler that it's a generic
  get: <T extends unknown>(url: string, { lang, options = {}, ...params }: Params): Promise<HTTPResponse<T>> => {
    const isBaseUrlExternal = url.match(/^http/);

    const { headers: _, ...paramsWithoutHeaders } = params;
    const endpoint = `${!isBaseUrlExternal ? process.env.API_URL : ''}${url}${parseObject(paramsWithoutHeaders)}`;

    return fetch(endpoint, {
      headers: {
        'Accept-Language': typeof lang === 'string' ? lang : defaultLocale,
        'Content-type': 'application/json; charset=UTF-8',
        ...(!isBaseUrlExternal ? { 'SAAS-APP-TOKEN': baseKey } : {}),
        ...(process.env.NODE_ENV === 'development' && !isBaseUrlExternal ? { 'Cache-Control': 'no-cache' } : {}),
        ...(params?.headers || {}),
      },
      method: 'GET',
      ...options,
    })
      .then(async (res) => {
        if (res.body) {
          const textBody = await res.text();

          let body: any = {};
          if (textBody) {
            try {
              body = JSON.parse(textBody);
            } catch (parseErr) {
              return Promise.reject({
                response: {
                  $ok: false,
                  status: res.status,
                  endpoint,
                  data: { detail: 'Non-JSON response', body: textBody.slice(0, 500) },
                },
              });
            }
          }

          if (Array.isArray(body)) {
            return {
              data: body,
              $ok: res.ok,
              status: res.status,
              endpoint,
            };
          }

          const { results: data, ...rest } = body;

          return {
            ...(data !== undefined ? rest : {}),
            data: data !== undefined ? data : rest,
            $ok: res.ok,
            status: res.status,
            endpoint,
          };
        }

        return { $ok: false, data: {} };
      })
      .then((res) => {
        if (res.$ok) {
          return Promise.resolve(res);
        }

        return Promise.reject({ response: res });
      });
  },

  post: <T extends unknown>(
    url: string,
    body: any,
    { formData, lang, options = {}, ...params }: Params,
  ): Promise<HTTPResponse<T>> => {
    const isBaseUrlExternal = url.match(/^http/);
    const endpoint = `${!isBaseUrlExternal ? process.env.API_URL : ''}${url}`;

    return fetch(endpoint, {
      ...params,
      headers: {
        'Accept-Language': typeof lang === 'string' ? lang : defaultLocale,
        ...(!isBaseUrlExternal ? { 'SAAS-APP-TOKEN': baseKey } : {}),
        ...(process.env.NODE_ENV === 'development' && !isBaseUrlExternal ? { 'Cache-Control': 'no-cache' } : {}),
        ...(params ? params.headers : {}),
        ...(formData ? {} : { 'Content-type': 'application/json; charset=UTF-8' }),
      },
      body: formData ? body : JSON.stringify(body),
      method: 'POST',
      ...options,
    })
      .then(async (res) => {
        if (res.body) {
          const textBody = await res.text();
          const body = Boolean(textBody) ? JSON.parse(textBody) : {};

          if (Array.isArray(body)) {
            return {
              data: body,
              $ok: res.ok,
              endpoint,
            };
          }

          const { results: data, ...rest } = body;

          return {
            ...(data !== undefined ? rest : {}),
            data: data !== undefined ? data : rest,
            $ok: res.ok,
            endpoint,
          };
        }

        return { $ok: false, data: { status: res.status }, endpoint };
      })
      .then((res) => {
        if (res.$ok) {
          return Promise.resolve(res);
        }

        return Promise.reject({ response: res });
      })
      .catch((res) => {
        const errors = res?.response?.data || {};
        const hasErrors = Object.keys(errors).length > 0;

        return Promise.reject({
          ...res,
          response: {
            data: hasErrors
              ? errors
              : {
                  detail: 'Oops.. Service currently is not working!',
                },
          },
        });
      });
  },
};
